import { useEffect, useState, useCallback, createContext, useContext } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { LayoutGrid, Box, Users, Monitor, AlertTriangle, Building2, ChevronDown, LogOut, Fingerprint, Database } from "lucide-react";
import { attPath } from "@/lib/attHost";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useSEO } from "@/lib/seo";
import { ScannerProvider } from "@/context/ScannerContext";
import ScannerStatus from "@/components/attendance/ScannerStatus";

const AttContext = createContext(null);
export const useAtt = () => useContext(AttContext);

const item = "flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm font-medium transition-colors";
const active = ({ isActive }) => `${item} ${isActive ? "bg-emerald-50 text-emerald-800" : "text-slate-600 hover:bg-slate-100"}`;

export default function AttendanceLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [me, setMe] = useState(null);
  const [centers, setCenters] = useState([]);
  const [batches, setBatches] = useState([]);
  const [candidates, setCandidates] = useState([]);
  const [candState, setCandState] = useState({ status: "loading", error: null });
  const [candOpen, setCandOpen] = useState(true);
  useSEO({ title: "Fingerprint Dashboard — Mashara Attendance", description: "Biometric enrollment dashboard.", path: "/attendance/app", noindex: true });

  const reload = useCallback(() => {
    api.get("/attendance/centers").then((r) => setCenters(r.data)).catch(() => {});
    api.get("/attendance/batches").then((r) => setBatches(r.data)).catch(() => {});
    setCandState((s) => (s.status === "loaded" ? s : { status: "loading", error: null }));
    api.get("/attendance/candidates").then((r) => { setCandidates(r.data); setCandState({ status: "loaded", error: null }); })
      .catch((e) => setCandState({ status: "error", error: e.response?.data?.detail || e.message || "Request failed" }));
  }, []);

  useEffect(() => {
    if (user === null) { navigate(attPath("/")); return; }
    if (!user) return;
    api.get("/attendance/me").then((r) => { setMe(r.data); reload(); }).catch(() => navigate(attPath("/")));
  }, [user, navigate, reload]);

  if (!me) return <div className="py-32 text-center text-slate-500">Loading…</div>;
  const isAdmin = me.role === "admin";
  const withBatch = candidates.map((c) => ({ ...c, batch_name: batches.find((b) => b.id === c.batch_id)?.name }));

  return (
    <ScannerProvider>
      <AttContext.Provider value={{ me, isAdmin, centers, batches, candidates: withBatch, candState, reload }}>
        <div className="att-shell min-h-screen bg-slate-50 flex" data-testid="attendance-dashboard">
          <aside className="att-sidebar print-hide w-64 shrink-0 bg-white border-r border-slate-200 flex flex-col" data-testid="att-sidebar">
            <div className="flex items-center gap-2 px-5 h-16 border-b border-slate-100">
              <span className="w-9 h-9 rounded-xl bg-emerald-700 text-white flex items-center justify-center"><Fingerprint className="w-5 h-5" /></span>
              <span className="font-display font-bold text-lg text-slate-900">Mashara <span className="text-emerald-700">Attendance</span></span>
            </div>
            <p className="px-5 pt-5 pb-2 text-[11px] font-semibold uppercase tracking-widest text-slate-400">Menu</p>
            <nav className="px-3 space-y-1 flex-1">
              <NavLink to={attPath("/app")} end className={active} data-testid="att-nav-dashboard"><LayoutGrid className="w-4 h-4" /> Dashboard</NavLink>
              <NavLink to={attPath("/app/batches")} className={active} data-testid="att-nav-batches"><Box className="w-4 h-4" /> Batches</NavLink>
              <button onClick={() => setCandOpen(!candOpen)} className={`${item} w-full text-slate-600 hover:bg-slate-100 justify-between`} data-testid="att-nav-candidates">
                <span className="flex items-center gap-3"><Users className="w-4 h-4" /> Candidates</span><ChevronDown className={`w-4 h-4 transition-transform ${candOpen ? "rotate-180" : ""}`} />
              </button>
              {candOpen && (
                <div className="ml-7 space-y-1">
                  <NavLink to={attPath("/app/candidates/register")} className={active} data-testid="att-nav-register">Register</NavLink>
                  <NavLink to={attPath("/app/candidates/view")} className={active} data-testid="att-nav-view">View</NavLink>
                  <NavLink to={attPath("/app/candidates/enhanced")} className={active} data-testid="att-nav-enhanced">Enhanced Fingerprint</NavLink>
                  <NavLink to={attPath("/app/candidates/enhance-lab")} className={active} data-testid="att-nav-enhance-lab">Enhancement Lab</NavLink>
                </div>
              )}
              <NavLink to={attPath("/app/software")} className={active} data-testid="att-nav-software"><Monitor className="w-4 h-4" /> Software</NavLink>
              <NavLink to={attPath("/app/error")} className={active} data-testid="att-nav-error"><AlertTriangle className="w-4 h-4" /> Error / Audit</NavLink>
              {isAdmin && <NavLink to={attPath("/app/centers")} className={active} data-testid="att-nav-centers"><Building2 className="w-4 h-4" /> Centers & Logins</NavLink>}
              {isAdmin && <NavLink to={attPath("/app/migration")} className={active} data-testid="att-nav-migration"><Database className="w-4 h-4" /> Data Migration</NavLink>}
            </nav>
            <div className="p-4 border-t border-slate-100">
              <p className="text-sm font-semibold text-slate-900 truncate" data-testid="attendance-me">{me.name}</p>
              <p className="text-xs text-slate-500 truncate">{isAdmin ? "Admin · all centers" : `Center: ${me.center?.name || "unassigned"}`}</p>
              <button data-testid="attendance-logout" onClick={async () => { await logout(); navigate(attPath("/")); }} className="mt-3 flex items-center gap-2 text-xs font-semibold text-slate-600 hover:text-red-600"><LogOut className="w-3.5 h-3.5" /> Sign out</button>
            </div>
          </aside>
          <div className="flex-1 min-w-0">
            <header className="att-topbar print-hide h-16 bg-white border-b border-slate-200 flex items-center justify-between px-6 sticky top-0 z-30">
              <p className="text-sm text-slate-500">Mantra L1 Biometric Enrollment</p>
              <div className="w-[420px] max-w-[50vw]"><ScannerStatus compact /></div>
            </header>
            <main className="att-main p-6"><Outlet /></main>
          </div>
        </div>
      </AttContext.Provider>
    </ScannerProvider>
  );
}
